import type { BillingWebhookRegistrationRecord } from "../reconciliation/types.ts";
import type { BillingWebhookKind } from "../types.ts";
import type { BillingWebhookDeliveryHealthRow } from "./delivery-health.ts";
import type { BillingWebhookReconciliationOutcome } from "./stages.ts";

export type BillingWebhookRouteMountState = true | false | "unknown";

export interface BillingWebhookOperationalStatus {
	connectionId: string;
	delivery: {
		lastAcceptedAt?: Date;
		lastReceivedAt?: Date;
		lastRejectedAt?: Date;
		lastRejectionCode?: string;
		lastRejectionMessage?: string;
	};
	kind: BillingWebhookKind;
	provider: "mollie";
	reconciliation: {
		lastCompletedAt?: Date;
		lastError?: string;
		lastFailedAt?: Date;
		lastOutcome?: BillingWebhookReconciliationOutcome;
		lastStartedAt?: Date;
	};
	registration: {
		lastReconciledAt?: Date;
		lastVerifiedAt?: Date;
		providerWebhookId?: string;
		status: "pending" | "active" | "error" | "disabled";
	};
	route: {
		endpoint: string;
		mounted: BillingWebhookRouteMountState;
	};
	verification: {
		previousSigningSecretsConfigured: number;
		signingSecretConfigured: boolean;
		strategy: "authoritative_refetch" | "signature_and_refetch";
	};
}

function registrationStatus(
	status: BillingWebhookRegistrationRecord["status"] | undefined,
): BillingWebhookOperationalStatus["registration"]["status"] {
	if (status === "error") {
		return "error";
	}
	if (status === "disabled") {
		return "disabled";
	}
	if (status === "active") {
		return "active";
	}
	return "pending";
}

export function billingWebhookOperationalStatus(input: {
	connectionId: string;
	delivery?: BillingWebhookDeliveryHealthRow;
	endpoint: string;
	kind: BillingWebhookKind;
	mounted: BillingWebhookRouteMountState;
	previousSigningSecretsConfigured: number;
	registration?: BillingWebhookRegistrationRecord;
	signingSecretConfigured: boolean;
}): BillingWebhookOperationalStatus {
	const delivery = input.delivery;
	return {
		connectionId: input.connectionId,
		delivery: {
			...(delivery?.lastAcceptedAt
				? { lastAcceptedAt: delivery.lastAcceptedAt }
				: {}),
			...(delivery?.lastReceivedAt
				? { lastReceivedAt: delivery.lastReceivedAt }
				: {}),
			...(delivery?.lastRejectedAt
				? { lastRejectedAt: delivery.lastRejectedAt }
				: {}),
			...(delivery?.lastRejectionCode
				? { lastRejectionCode: delivery.lastRejectionCode }
				: {}),
			...(delivery?.lastRejectionMessage
				? { lastRejectionMessage: delivery.lastRejectionMessage }
				: {}),
		},
		kind: input.kind,
		provider: "mollie",
		reconciliation: {
			...(delivery?.lastReconciliationCompletedAt
				? { lastCompletedAt: delivery.lastReconciliationCompletedAt }
				: {}),
			...(delivery?.lastReconciliationError
				? { lastError: delivery.lastReconciliationError }
				: {}),
			...(delivery?.lastReconciliationFailedAt
				? { lastFailedAt: delivery.lastReconciliationFailedAt }
				: {}),
			...(delivery?.lastReconciliationOutcome
				? { lastOutcome: delivery.lastReconciliationOutcome }
				: {}),
			...(delivery?.lastReconciliationStartedAt
				? { lastStartedAt: delivery.lastReconciliationStartedAt }
				: {}),
		},
		registration: {
			status: registrationStatus(input.registration?.status),
			...(input.registration?.lastReconciledAt
				? { lastReconciledAt: input.registration.lastReconciledAt }
				: {}),
			...(input.registration?.lastVerifiedAt
				? { lastVerifiedAt: input.registration.lastVerifiedAt }
				: {}),
			...(input.registration?.providerWebhookId
				? { providerWebhookId: input.registration.providerWebhookId }
				: {}),
		},
		route: {
			endpoint: input.endpoint,
			mounted: input.mounted,
		},
		verification: {
			previousSigningSecretsConfigured: input.previousSigningSecretsConfigured,
			signingSecretConfigured: input.signingSecretConfigured,
			strategy:
				input.kind === "classic"
					? "authoritative_refetch"
					: "signature_and_refetch",
		},
	};
}
